import { useEffect, useMemo, useState } from "react";
import { usePilotAddresses, usePilotAgents } from "apis/pilot";
import { indexAddresses, suggestAddresses, withAddress } from "utils/addressSuggest";
import { suggestPeople, withPerson } from "utils/peopleSuggest";

const asPropertyRecord = (property) => ({
  id: property.propertyId,
  // Told apart from another record of the same address by what it is.
  name: `${property.address}${property.isSettled ? " (settled)" : ""}${property.isMaster ? " (development)" : ""}`,
  kind: "property",
});
// An agent is a user record.
const asAgentRecord = (agent) => ({
  id: agent._id,
  name: agent.name,
  kind: "user",
});

// Pilot's suggestions for what is being typed: the platform's addresses, or agents when no address fits, matched in
// the browser (no request per keystroke). Nothing is fetched or suggested while `isActive` is false. A pick hands
// `onPick` the text with it written in, its record for Pilot, and the words written for it.
export default function usePilotSuggestions({ text, isActive, onPick }) {
  const { data: propertyAddresses } = usePilotAddresses({ enabled: isActive });
  const addressIndex = useMemo(() => indexAddresses(propertyAddresses), [propertyAddresses]);
  const { data: agents = [] } = usePilotAgents({ enabled: isActive });
  const addresses = useMemo(
    () => (isActive ? suggestAddresses(addressIndex, text) : []),
    [addressIndex, isActive, text],
  );
  const people = useMemo(
    () => (isActive && !addresses.length ? suggestPeople(text, { agents }) : []),
    [addresses, agents, isActive, text],
  );
  // Escape closes the list until the words change; the arrow keys move through it, and Enter sends the message unless
  // one has been moved to.
  const [closedFor, setClosedFor] = useState(null);
  const [highlighted, setHighlighted] = useState(-1);
  const suggestions = closedFor === text ? [] : addresses.length ? addresses : people;
  useEffect(() => setHighlighted(-1), [text]);

  const pick = (suggestion) =>
    suggestion.propertyId
      ? onPick(withAddress(text, suggestion), asPropertyRecord(suggestion), suggestion.address)
      : onPick(withPerson(text, suggestion), asAgentRecord(suggestion), suggestion.name);

  const onKeyDown = (event) => {
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((at) => (at + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((at) => (at <= 0 ? suggestions.length - 1 : at - 1));
    } else if (event.key === "Escape") {
      setClosedFor(text);
    } else if (event.key === "Enter" && !event.shiftKey && highlighted >= 0) {
      event.preventDefault();
      pick(suggestions[highlighted]);
    }
  };

  return { suggestions, highlighted, setHighlighted, pick, onKeyDown };
}
